import { ChartBarIcon } from "@heroicons/react/outline";
import type { CustomHeaderGroupProps } from "ag-grid-react";
import { useEffect, useState } from "react";

import { SmartColumnHeaderIcon } from "../../ColumnIcons";
import type { VersionDiffStatus, VersionPreviewColumnConfigTarget } from "../../types";

import { BasicTooltip } from "@/components/ui/Tooltip";
import { cn } from "@/lib/utils";
import type { ColumnType } from "@/queries/smart-tables/types";
import {
  columnTypeSupportsRequestLogAnalytics,
  columnTypeSupportsTraceAnalytics,
} from "@/routes/smart-table/utils/columnTypesWithRequestLogAnalytics";
import { stopHeaderInteraction } from "@/routes/smart-table/utils/headerInteractionGuards";

interface VersionPreviewHeaderProps {
  displayName?: string;
  column: {
    getColDef: () => {
      headerComponentParams?: {
        columnType?: ColumnType;
        config?: Record<string, unknown> | null;
        title?: string;
        diffStatus?: VersionDiffStatus;
        columnId?: string;
        columnTitle?: string;
        onOpenColumnConfig?: (target: VersionPreviewColumnConfigTarget) => void;
        onOpenColumnAnalytics?: (columnId: string, columnTitle: string) => void;
        parentColumnId?: string;
        compositionSource?: string;
        metricKey?: string | null;
      };
    };
  };
}

const VersionPreviewHeader = ({ displayName, column }: VersionPreviewHeaderProps) => {
  const headerParams = column.getColDef().headerComponentParams;
  const diffStatus = headerParams?.diffStatus;
  const columnId = headerParams?.columnId;
  const columnTitle = headerParams?.columnTitle ?? displayName;
  const canOpenConfig = Boolean(columnId && columnTitle && headerParams?.onOpenColumnConfig);
  const canOpenAnalytics = Boolean(
    columnId &&
      columnTitle &&
      headerParams?.onOpenColumnAnalytics &&
      (columnTypeSupportsRequestLogAnalytics(headerParams.columnType) ||
        columnTypeSupportsTraceAnalytics(headerParams.columnType)),
  );

  return (
    <div className="relative flex h-full w-full items-center">
      <button
        type="button"
        className={cn(
          "absolute inset-0 rounded-sm",
          canOpenConfig &&
            "cursor-pointer transition-colors hover:bg-accent/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30",
          !canOpenConfig && "cursor-default",
        )}
        onClick={() => {
          if (!canOpenConfig || !columnId || !columnTitle) {
            return;
          }

          headerParams?.onOpenColumnConfig?.({
            columnId,
            columnTitle,
            parentColumnId: headerParams.parentColumnId,
            compositionSource: headerParams.compositionSource,
          });
        }}
        aria-label={canOpenConfig ? `View saved configuration for ${columnTitle}` : undefined}
      />

      <div className="pointer-events-none flex min-w-0 flex-1 items-center gap-1.5 pr-8">
        <SmartColumnHeaderIcon
          columnType={headerParams?.columnType}
          config={headerParams?.config ?? null}
          metricKey={headerParams?.metricKey}
          className="mt-px"
          title={headerParams?.title ?? displayName}
        />
        <span className="truncate text-sm font-medium">{displayName ?? ""}</span>
      </div>

      {canOpenAnalytics && columnId && columnTitle && (
        <BasicTooltip content={`Analytics for ${columnTitle}`}>
          <button
            type="button"
            data-smart-table-header-action
            className="absolute right-1 top-1/2 z-10 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-gray-100 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
            aria-label={`Open analytics for ${columnTitle}`}
            onClick={event => {
              stopHeaderInteraction(event);
              headerParams?.onOpenColumnAnalytics?.(columnId, columnTitle);
            }}
            onMouseDown={event => {
              stopHeaderInteraction(event, { preventDefault: true });
            }}
          >
            <ChartBarIcon className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </BasicTooltip>
      )}

      {diffStatus && (
        <span
          className={cn(
            "pointer-events-none absolute h-2 w-2 rounded-full",
            canOpenAnalytics ? "right-8" : "right-0.5",
            diffStatus === "added" && "bg-green-500/70",
            diffStatus === "removed" && "bg-red-500/70",
            diffStatus === "updated" && "bg-blue-500/70",
          )}
          title={`${diffStatus} column change`}
          aria-label={`${diffStatus} column change`}
        />
      )}
    </div>
  );
};

export interface VersionPreviewGroupHeaderParams {
  columnType?: ColumnType;
  config?: Record<string, unknown> | null;
  title?: string;
  diffStatus?: VersionDiffStatus;
  columnId?: string;
  columnTitle?: string;
  onOpenColumnConfig?: (target: VersionPreviewColumnConfigTarget) => void;
  onOpenColumnAnalytics?: (columnId: string, columnTitle: string) => void;
  parentColumnId?: string;
  compositionSource?: string;
  metricKey?: string | null;
}

export const VersionPreviewGroupHeader = ({
  displayName,
  columnGroup,
  setExpanded,
  columnType,
  config,
  title,
  diffStatus,
  columnId,
  columnTitle,
  onOpenColumnConfig,
  onOpenColumnAnalytics,
  parentColumnId,
  compositionSource,
  metricKey,
}: CustomHeaderGroupProps & VersionPreviewGroupHeaderParams) => {
  const providedGroup = columnGroup.getProvidedColumnGroup();
  const [isExpanded, setIsExpanded] = useState(() => providedGroup.isExpanded());

  useEffect(() => {
    const handleExpandedChanged = () => {
      setIsExpanded(providedGroup.isExpanded());
    };

    providedGroup.addEventListener("expandedChanged", handleExpandedChanged);

    return () => {
      providedGroup.removeEventListener("expandedChanged", handleExpandedChanged);
    };
  }, [providedGroup]);

  const resolvedColumnTitle = columnTitle ?? title ?? displayName;
  const canOpenConfig = Boolean(columnId && resolvedColumnTitle && onOpenColumnConfig);
  const canOpenAnalytics = Boolean(
    columnId &&
      resolvedColumnTitle &&
      onOpenColumnAnalytics &&
      columnTypeSupportsRequestLogAnalytics(columnType),
  );

  return (
    <div className="ag-header-group-cell-label flex h-full w-full items-center gap-1.5">
      <button
        type="button"
        className={cn(
          "flex h-full min-w-0 flex-1 items-center gap-1.5 rounded-sm text-left",
          canOpenConfig &&
            "cursor-pointer transition-colors hover:bg-accent/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30",
          !canOpenConfig && "cursor-default",
        )}
        onClick={event => {
          stopHeaderInteraction(event, { preventDefault: true });

          if (!canOpenConfig || !columnId || !resolvedColumnTitle) {
            return;
          }

          onOpenColumnConfig?.({
            columnId,
            columnTitle: resolvedColumnTitle,
            parentColumnId,
            compositionSource,
          });
        }}
        onMouseDown={event => {
          stopHeaderInteraction(event, { preventDefault: true });
        }}
        aria-label={
          canOpenConfig ? `View saved configuration for ${resolvedColumnTitle}` : undefined
        }
      >
        <SmartColumnHeaderIcon
          columnType={columnType}
          config={config ?? null}
          metricKey={metricKey}
          className="mt-px"
          title={title ?? displayName}
        />

        <span className="min-w-0 truncate text-sm font-medium">{displayName ?? ""}</span>
      </button>

      {canOpenAnalytics && columnId && resolvedColumnTitle && (
        <BasicTooltip content={`Analytics for ${resolvedColumnTitle}`}>
          <button
            type="button"
            data-smart-table-header-action
            className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-gray-100 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
            aria-label={`Open analytics for ${resolvedColumnTitle}`}
            onClick={event => {
              stopHeaderInteraction(event, { preventDefault: true });
              onOpenColumnAnalytics?.(columnId, resolvedColumnTitle);
            }}
            onMouseDown={event => {
              stopHeaderInteraction(event, { preventDefault: true });
            }}
          >
            <ChartBarIcon className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </BasicTooltip>
      )}

      <button
        type="button"
        className="flex flex-shrink-0 cursor-pointer items-center justify-center opacity-80 hover:opacity-100"
        onClick={event => {
          stopHeaderInteraction(event, { preventDefault: true });
          setExpanded(!isExpanded);
        }}
        onMouseDown={event => {
          stopHeaderInteraction(event, { preventDefault: true });
        }}
        title={isExpanded ? "Collapse group" : "Expand group"}
        aria-label={isExpanded ? "Collapse group" : "Expand group"}
      >
        {isExpanded ? (
          <span className="ag-header-icon ag-header-expand-icon ag-header-expand-icon-expanded">
            <span className="ag-icon ag-icon-expanded" aria-hidden="true" />
          </span>
        ) : (
          <span className="ag-header-icon ag-header-expand-icon ag-header-expand-icon-collapsed">
            <span className="ag-icon ag-icon-contracted" aria-hidden="true" />
          </span>
        )}
      </button>

      {diffStatus && (
        <span
          className={cn(
            "ml-auto h-2 w-2 flex-shrink-0 rounded-full",
            diffStatus === "added" && "bg-green-500/70",
            diffStatus === "removed" && "bg-red-500/70",
            diffStatus === "updated" && "bg-blue-500/70",
          )}
          title={`${diffStatus} column change`}
          aria-label={`${diffStatus} column change`}
        />
      )}
    </div>
  );
};

export default VersionPreviewHeader;
